import { TheFoldScore } from "../TheFoldScore";
import { Clip } from "../models";
import { secondsToTime } from "../utils";
import { lyrics } from "../lyrics";

import { LitElement, css, html } from 'lit';
import { property, customElement, queryAll, query } from 'lit/decorators.js';

import { styles } from '../styles/shared-styles';
import { unsafeHTML } from "lit/directives/unsafe-html.js";

const locales = {
  "cs": {
    "allrightsreserved": "Všechna práva vyhrazena.",
    "legacyapp": "Původní aplikace",
    "lyrics": "Text",
    "no": "Ne",
    "playbackfinished": "Přehrávání skončilo.",
    "playbackfinishedquestion": "Chcete si poslechnout jinou verzi?",
    "privacy": "Soukromí",
    "regenerate": "Načti jinou verzi",
    "storesLabel": "Offline verze",
    "support": "Podpora",
    "whatdoesitmean": "Co to znamená?",
    "yes": "Ano",
  },
  "en": {
    "allrightsreserved": "All rights reserved.",
    "legacyapp": "Legacy app",
    "lyrics": "Lyrics",
    "no": "No",
    "playbackfinished": "Playback finished.",
    "playbackfinishedquestion": "Do you want to listen to another version?",
    "privacy": "Privacy",
    "regenerate": "Load another version",
    "storesLabel": "Listen offline",
    "support": "Support",
    "whatdoesitmean": "What does it mean?",
    "yes": "Yes",
  }
}

@customElement('app-album')
export class AppAlbum extends LitElement {

  // For more information on using properties and state in lit
  // check out this link https://lit.dev/docs/components/properties/
  @property() tracklist: Clip[];
  @property() tracklistId: string = "";
  @property() activeTrackIndex = 1;
  @property() isPlaying = false;
  @property() currentTrackProgressPercentage = 0;
  @property() currentTrackProgressSeconds = 0;
  @property() currentTrack: Clip;
  @property() currentLanguage: string = localStorage.getItem("lang") || navigator?.language?.substring(0,2) || "en";

  @query("button#play")
  _playButton!: NodeListOf<HTMLButtonElement>;
  @query("button#pause")
  _pauseButton!: NodeListOf<HTMLButtonElement>;
  @query("button#previous")
  _previousButton!: NodeListOf<HTMLButtonElement>;
  @query("button#next")
  _nextButton!: NodeListOf<HTMLButtonElement>;
  @queryAll("audio")
  _audio!: NodeListOf<HTMLAudioElement>;

  constructor() {
    super();
    const score = TheFoldScore({
      algorithm: Math.round(Math.random()),
      crossfadeTime: 0,
      minDuration: 900,
    });
    
    // auto-generate score and load
    // if (setAlbumVersion != null && setAlbumVersion.length > 0) {
    //   tracklist = score.load(setAlbumVersion) || [];
    // } else {
    //   tracklist = score.generate();
    // }
    this.tracklist = score.generate();
    this.tracklistId = score.getCurrentTracklistId();
    this.currentTrack = this.tracklist[0];
  }

  static styles = [
    styles,
    css`
    main {
      position: relative;
      padding: 0;
      margin: 0;
    }

    .mobile-only { display: none; }

    #header {
      position: relative;
      text-align: right;
      width: 90%;
      max-width: 320px;
      margin: 0 auto;

      img { max-width: 100%; }
    }

    #stores {
      position: absolute;
      top: 60px;
      left: -150px;
      width: 140px;
    }

    #stores-label {
      font-weight: 700;
      font-size: 0.75em;
      text-transform: uppercase;
      color: #888;
      border-bottom: 1px solid #888;
      text-align: left;
      padding-bottom: 1px;
      margin-bottom: 6px;
    }
    
    #stores-content {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: center;
      gap: 10px;

      a {
        display: block;
        background: transparent;
        opacity: 1;
        cursor: pointer;
        text-decoration: none;
        width: 100%;
        height: auto;
        padding: 0;

        &:hover {
          opacity: 0.5;
        }

        svg {
          display: block;
          fill: #888;
          border-radius: 5px;
          background-color: #fff;
        }
      }
    }

    #menu {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }

    #language-toggle {
      display: block;
      border: none;
      background: transparent;
      cursor: pointer;
      font-size: 0.75em;
      font-weight: 500;
      font-family: "Raleway", sans-serif;
      color: #fff;
      background-color: #888;
      text-decoration: none;
      padding: 5px;
      width: 30px;
      height: 30px;
      border-radius: 5px;
      margin: 10px 0 10px 5px;

      &:hover { opacity: 0.5; }
    }

    #infotext-toggle {
      display: block;
      border: none;
      background: transparent;
      opacity: 1;
      cursor: pointer;
      width: 40px;
      height: 40px;
      padding: 0;
      margin: 10px 0 10px 10px;

      svg { fill: #000; }

      &:hover {
        opacity: 0.5;
      }
    }

    #track-info {
      text-align: center;
      font-weight: 500;
      font-size: 1.2em;
      margin-top: 20px;
    }

    .version-name { opacity: 0.5; font-size: 70%; font-weight: 300; white-space: nowrap; }

    #track-timing {
      text-align: center;
    }

    button svg { fill: #fff; }

    #controls {
      text-align: center;
      display: flex;
      justify-content: space-evenly;
      align-items: center;
      margin: 20px auto;
      max-width: 320px;

      button {
        border: none;
        background: transparent;
        font-size: 2em;
        font-weight: 700;
        opacity: 1;
        cursor: pointer;
        vertical-align: top;

        &:hover {
          opacity: 0.5;
        }

        &#previous, &#next {
          svg {
            width: 35px;
            height: 35px;
          }
          &.disabled {
            opacity: 0.2;
            cursor: initial;
          }
        }
        &#play, &#pause {
          svg {
            width: 60px;
            height: 60px;
          }
        }
      }
    }

    #track-progress-wrapper { 
      position: relative;
      padding: 15px 0 25px;

      &:hover #handle { opacity: 1; }
    }

    #track-progress-bar {
      position: relative;
      margin: 0 auto;
      width: 90%;
      height: 4px;
      border-radius: 2px;
      max-width: 320px;
      background-color: #bbb;

      #progress {
        position: absolute;
        top: 0;
        left: 0;
        background-color: #444;
        height: 4px;
        border-radius: 2px;
        transition: width 0.2s linear;
      }

      #handle {
        display: none;
        opacity: 0;
        position: absolute;
        top: -6px;
        width: 16px;
        height: 16px;
        border-radius: 8px;
        background-color: #000;
        transition: left,opacity 0.2s linear;
        transform: translateX(-8px);
        cursor: pointer;
      }
    }

    #playlist {
      max-width: 320px;
      margin: 10px auto;
      padding: 0;
      list-style-position: inside;
    }

    #playlist li {
      position: relative;
      cursor: pointer;
      margin: 5px 0;

      &.active { font-weight: 700;}
      &:hover span.track-name { text-decoration: underline; }
      .track-duration {
        opacity: 0.5;
        font-weight: 300;
        position: absolute;
        right: -40px;
      }
    }

    #lyrics {
      width: 90%;
      max-width: 320px;
      margin: 40px auto;

      h2 { font-size: 1.2em; font-weight: 500; }
      #lyrics-content { text-align: justify; font-size: 0.85em; }
    }

    #regenerate-wrapper {
      width: 90%;
      max-width: 320px;
      margin: 40px auto 40px;
      text-align: center;

      button {
        border: none;
        font-family: "Raleway", sans-serif;
        padding: 10px 20px;
        border-radius: 5px;
        border: 1px solid;
        font-size: 1em;
        font-weight: 500;
        cursor: pointer;

        &:hover { opacity: 0.5; }
      }
    }

    .horizontal-separator {
      height: 1px;
      width: 90%;
      max-width: 320px;
      margin: 40px auto;
      background-color: #bbb;
    }

    #footer {
      width: 90%;
      max-width: 320px;
      margin: 40px auto;
      text-align: center;
      font-size: 0.8em;
      padding-bottom: 40px;
      color: #888;

      & > div { margin: 15px 0; }

      a {
        color: #888;

        &:hover { text-decoration: none; }
      }

      #links a {
        white-space: nowrap;
        margin: 0 10px;
      }
    }

    #socials {
      display: flex;
      justify-content: center;
      gap: 10px;
    }

    .social a svg {
      display: block;
      fill: #888;
      width: 20px;
      height: 20px;

      &:hover { opacity: 0.5; }
    }

    #regenerate-after-play-wrapper {
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      display: none;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 20px;
      font-weight: 500;
      font-size: 16px;
      background-color: #f5f5f5;
      color: #000;

      &.visible { display: flex; }

      #regenerate-question { text-align: center; }

      #regenerate-choice {
        display: flex;
        flex-direction: row;
        justify-content: center;
        gap: 20px;
      }
      
      button {
        border: none;
        font-family: "Raleway", sans-serif;
        padding: 10px 20px;
        border-radius: 5px;
        border: 1px solid;
        font-size: 1em;
        font-weight: 500;
        cursor: pointer;

        &:hover { opacity: 0.5; }
      }

      #rcb-yes {
        background-color: #000;
        border-color: #000;
        color: #fff;
      }

      #rcb-no {
        border-color: #000;
        background-color: #f5f5f5;
      }

      #regenerate-why {
        color: #888;
        font-size: 14px;

        &:hover { text-decoration: none; }
      }
    }

    @media (prefers-color-scheme: dark) {
      #stores-label { border-color: #444; } 
      #stores-content a svg { background-color: #000; }
      #language-toggle { color: #000; }
      #infotext-toggle svg { fill: #fff; }
      button svg { fill: #fff; }
      #track-progress-bar { background-color: #444; }
      #track-progress-bar #progress { background-color: #bbb; }
      #regenerate-wrapper button { color: #000; background-color: #fff; border-color: #fff; }
      .horizontal-separator { background-color: #444; }
      #regenerate-after-play-wrapper {
        background-color: #181818;
        color: #fff;
        #rcb-yes {
          background-color: #fff;
          border-color: #fff;
          color: #000;
        }
        #rcb-no {
          border-color: #fff;
          background-color: #181818;
        }
      }
    }

    @media (prefers-color-scheme: light) {
      #stores-label { border-color: #bbb; }
      #stores-content a svg { background-color: #fff; }
      #language-toggle { color: #fff; }
      #infotext-toggle svg { fill: #000; }
      button svg { fill: #000; }
      #track-progress-bar { background-color: #bbb; }
      #track-progress-bar #progress { background-color: #444; }
      #regenerate-wrapper button { color: #fff; background-color: #000; border-color: #000; }
      .horizontal-separator { background-color: #bbb; }
      #regenerate-after-play-wrapper {
        background-color: #f5f5f5;
        color: #000;
        #rcb-yes {
          background-color: #000;
          border-color: #000;
          color: #fff;
        }
        #rcb-no {
          border-color: #000;
          background-color: #f5f5f5;
        }
      }
    }

    @media screen and (max-width: 640px) {
      .mobile-only { display: initial; }

      #header {
        width: 100%;
        max-width: initial;
      }

      #stores {
        position: initial;
        width: auto;
      }

      #stores-label {
        text-align: center;
        padding-top: 10px;
        margin-bottom: 0;
        border: none;
      }

      #stores-content {
        flex-direction: row;
        justify-content: space-evenly;
        align-items: center;
        gap: 0;
        padding: 5px 0 10px;
        border-bottom: 1px solid #888;
        background-color: #f5f5f5;
        border-bottom-color: #bbb;

        a {
          width: 30%;
          max-width: 200px;
          svg {
            fill: #000; background-color: transparent;
          }
        }
      }

      #language-toggle {
        margin-left: 15px;
      }

      #infotext-toggle {
        margin: 10px;
      }
    }

    @media screen and (prefers-color-scheme: dark) and (max-width: 640px) {
      #stores {
        background-color: #181818;
        border-bottom-color: #444;
      }
      #stores-content { background-color: #181818; }
      #stores-content a svg { fill: #fff; background-color: transparent; }
    }

    @media screen and (prefers-color-scheme: light) and (max-width: 640px) {
      #stores {
        background-color: #f5f5f5;
        border-bottom-color: #bbb;
      }
      #stores-content { background-color: #f5f5f5; }
      #stores-content a svg { fill: #000; background-color: transparent; }
    }
  `];

  async firstUpdated() {
    // this method is a lifecycle even in lit
    // for more info check out the lit docs https://lit.dev/docs/components/lifecycle/
  }

  share() {
    if ((navigator as any).share) {
      (navigator as any).share({
        title: 'PWABuilder pwa-starter',
        text: 'Check out the PWABuilder pwa-starter!',
        url: 'https://github.com/pwa-builder/pwa-starter',
      });
    }
  }

  private _reportTrackUpdateToAPI(status?: string) {
    const trackToReport = this.currentTrack;
    const reportObj = {
      version: this.tracklistId,
      track_id: trackToReport.id,
      track_name: (trackToReport.trackNameDisplay ? trackToReport.trackNameDisplay : trackToReport.trackName) + " v" + trackToReport.trackVersion
    } 
    const accessRecordId = trackToReport.accessRecordId;

    if (accessRecordId && status) {
      fetch("/access.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ "rec_id": accessRecordId, "status": status }),
      });
      trackToReport.accessRecordStatus = status;
    } else if (!trackToReport.accessRecordStatus) {
      fetch("/access.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(reportObj),
      }).then((res) => {
        if (!res.ok) {
          throw new Error(res.statusText);
        }
        if (res) return res.json();
        return { id: 0 };
      }).then((responseData) => {
        if (responseData !== null && responseData.id !== null) {
          trackToReport.accessRecordId = responseData.id;
          trackToReport.accessRecordStatus = "skip";
        }
      })
      .catch(() => {});
    }
  }

  private _stopAndResetAllTracks() {
    this._audio.forEach((element) => {
      element.pause();
      element.currentTime = 0;
    });
  }

  private _updateCurrentTrack() {
    this.currentTrack = this.tracklist[this.activeTrackIndex - 1];
  }

  private _toggleLanguage() {
    this.currentLanguage = this.currentLanguage === "cs" ? "en" : "cs";
    localStorage.setItem("lang", this.currentLanguage);
  }

  private _onPreviousClick() {
    if (this.activeTrackIndex === 1) return;

    delete this.currentTrack.accessRecordId;
    delete this.currentTrack.accessRecordStatus;
    if (this.activeTrackIndex > 1) {
      this.activeTrackIndex--;
      this._updateCurrentTrack();
      this._stopAndResetAllTracks();
      const prevTrack = this.renderRoot.querySelector(`#audio${this.activeTrackIndex}`) as HTMLAudioElement;
      prevTrack?.play();
      this.isPlaying = true;
      this._reportTrackUpdateToAPI();
    }
  }

  private _onPlayClick() {
    const activeTrack = this.renderRoot.querySelector(`#audio${this.activeTrackIndex}`) as HTMLAudioElement;
    activeTrack?.play();
    if (!activeTrack?.paused) {
      this.isPlaying = true;
      this._reportTrackUpdateToAPI();
    }
  }

  private _onPauseClick() {
    const activeTrack = this.renderRoot.querySelector(`#audio${this.activeTrackIndex}`) as HTMLAudioElement;
    activeTrack?.pause();
    if (activeTrack?.paused) {
      this.isPlaying = false;
    }
  }

  private _onNextClick() {
    if (this.activeTrackIndex === this.tracklist.length) return;

    delete this.currentTrack.accessRecordId;
    delete this.currentTrack.accessRecordStatus;
    if (this.activeTrackIndex < this.tracklist.length) {
      this.activeTrackIndex++;
      this._updateCurrentTrack();
      this._stopAndResetAllTracks()
      const nextTrack = this.renderRoot.querySelector(`#audio${this.activeTrackIndex}`) as HTMLAudioElement;
      nextTrack?.play();
      this.isPlaying = true;
      this._reportTrackUpdateToAPI();
    }
  }

  private _onPlaybackEnded() {
    // last track finished playing
    if (this.activeTrackIndex === this.tracklist.length) {
      this.activeTrackIndex = 1;
      this._updateCurrentTrack();
      this._stopAndResetAllTracks();
      this.isPlaying = false;
      this.renderRoot.querySelector("#regenerate-after-play-wrapper")?.classList.add("visible");
    } else {
      this._onNextClick();
    }
  }

  private _onTrackClick(ev: Event) {
    const target = ev.target as HTMLElement;
    const newTrackIndex = target.closest("li")?.getAttribute("data-index");
    if (newTrackIndex) {
      delete this.currentTrack.accessRecordId;
      delete this.currentTrack.accessRecordStatus;
      this._stopAndResetAllTracks();
      this.activeTrackIndex = parseInt(newTrackIndex, 10);
      this._updateCurrentTrack();
      const newTrack = this.renderRoot.querySelector(`#audio${this.activeTrackIndex}`) as HTMLAudioElement;
      newTrack?.play();
      this.isPlaying = true;
      this._reportTrackUpdateToAPI();
    }
  }

  private _onAudioTimeupdate(ev: Event) {
    const target = ev.target as HTMLAudioElement;
    this.currentTrackProgressPercentage = (target.currentTime / target.duration) * 100;
    this.currentTrackProgressSeconds = target.currentTime;

    if (this.currentTrackProgressPercentage >= 10 && this.currentTrack.accessRecordStatus === "skip") {
      this.currentTrack.accessRecordStatus = "partial";
      this._reportTrackUpdateToAPI("partial");
    }

    if (this.currentTrackProgressPercentage >= 90 && this.currentTrack.accessRecordStatus === "partial") {
      this.currentTrack.accessRecordStatus = "complete";
      this._reportTrackUpdateToAPI("complete");
    }
  }

  private _onRegenerateClick() {
    location.reload();
  }

  private _closeRegenerateAfterPlay() {
    this.renderRoot.querySelector("#regenerate-after-play-wrapper")?.classList.remove("visible");
  }

  render() {
    return html`
      <main>
        <div id="header">
          <div id="stores">
            <div id="stores-label">${ locales[this.currentLanguage].storesLabel }<span class="mobile-only">:</span></div>
            <div id="stores-content">
              <!--<a id="appstore" href="https://apps.apple.com/us/app/rouilleux-the-fold/id6744521676">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 237"><path d="M800,213.243c-0,13.032 -10.389,23.757 -23.013,23.757l-753.92,0c-12.636,0.012 -23.049,-10.712 -23.067,-23.757l-0,-189.455c0.018,-13.051 10.425,-23.782 23.067,-23.788l753.89,0c12.636,0 23.037,10.737 23.037,23.782l0.006,189.461Zm-317.388,-133.641l5.606,0.006c-0.276,-1.669 -0.402,-3.858 -0.402,-6.681l-0,-10.296c-0,-7.717 -3.689,-11.569 -10.996,-11.569c-3.791,0 -7.018,0.807 -9.602,2.419l1.262,4.088c2.133,-1.383 4.549,-2.078 7.307,-2.078c3.857,0 5.81,1.842 5.81,5.527l-0,0.515c-10.365,-0 -16.35,3.566 -16.35,10.47c0,4.721 3.564,8.231 8.575,8.231c3.509,0 6.279,-1.265 8.177,-3.852l0.163,0l0.45,3.22Zm-0.853,-13.932l0,4.317c0,3.282 -2.938,5.694 -6.099,5.694c-2.481,0 -4.266,-1.439 -4.266,-4.373c0,-3.963 3.461,-5.638 10.365,-5.638Zm203.061,13.932l0,-16.636c0,-7.307 -3.617,-11.91 -9.535,-11.91c-3.617,-0 -6.453,1.501 -8.412,4.547l-0.12,-0l-0,-16.798l-6.213,0l-0,40.797l6.213,-0l-0,-16.86c-0,-4.429 2.998,-6.618 6.218,-6.618c3.75,-0 5.643,2.531 5.643,7.536l-0,15.942l6.206,-0Zm-240.639,0.632c8.346,0 13.934,-6.103 13.928,-14.843c0,-8.517 -5.408,-14.329 -13.471,-14.329c-8.514,0 -13.922,6.153 -13.922,14.788c-0,8.281 5.3,14.384 13.465,14.384Zm0.235,-24.631c4.374,-0 7.246,4.255 7.24,9.949c-0,5.589 -2.932,10.074 -7.307,10.074c-4.356,-0 -7.3,-4.2 -7.3,-9.956c-0,-5.868 2.878,-10.067 7.367,-10.067Zm73.581,23.999l5.534,-0c-0.168,-2.302 -0.234,-4.832 -0.234,-7.537l-0,-33.26l-6.219,0l-0,16.053l-0.126,0c-1.491,-2.537 -4.146,-3.802 -7.884,-3.802c-7.072,-0 -12.251,6.159 -12.251,14.906c-0,8.342 4.993,14.272 11.741,14.272c4.188,0 7.192,-1.73 8.976,-5.123l0.175,-0l0.288,4.491Zm-0.919,-16.457l-0,4.659c-0,4.025 -2.764,7.425 -6.67,7.425c-4.38,-0 -7.3,-3.859 -7.3,-9.497c-0,-5.75 2.812,-9.838 7.42,-9.838c3.96,0 6.55,3.337 6.55,7.251Zm46.885,76.191l0,-9.51l-13.94,0l0,-14.167l-12.42,3.747l0,10.42l-8.304,0l0,9.485l8.304,-0l0,28.682c0,7.183 1.412,12.288 4.218,15.333c2.818,3.052 6.718,4.572 11.711,4.572c4.062,-0 7.343,-0.465 9.836,-1.396l-0.348,-9.602c-1.484,0.397 -3.359,0.589 -5.618,0.589c-4.921,0 -7.379,-3.504 -7.379,-10.532l0,-27.621l13.94,-0Zm-245.783,47.408l-25.524,-78.907l-15.695,0l-25.41,78.907l13.111,0l7.03,-23.174l25.639,0l7.378,23.174l13.471,0Zm-23.073,-32.9l-21.192,0l6.555,-20.606c1.719,-6.55 2.969,-11.506 3.75,-14.862l0.24,-0c1.941,7.803 3.269,12.759 3.978,14.862l6.669,20.606Zm423.428,-86.729c0.18,-0.93 0.252,-1.873 0.21,-2.822c0,-7.996 -4.482,-13.293 -11.627,-13.293c-7.925,0 -13.231,6.445 -13.231,15.024c0,8.566 5.18,14.093 13.928,14.093c3.624,-0 6.736,-0.577 9.38,-1.725l-0.961,-4.317c-2.248,0.856 -4.783,1.321 -7.541,1.321c-5.192,0 -8.695,-2.99 -8.815,-8.281l18.657,0Zm-5.702,-4.435l-12.949,0c0.403,-4.255 3.227,-7.363 6.796,-7.363c3.978,0 6.153,3.052 6.153,7.363Zm-44.999,78.287l-0.012,-12.059c-0.937,-0.148 -1.989,-0.235 -3.16,-0.235c-3.293,0.006 -6.502,1.073 -9.14,3.052c-2.878,2.257 -4.981,5.272 -6.321,9.012l-0.348,0l-0.463,-10.886l-11.014,0c0.259,5.967 0.379,11.941 0.367,17.914l-0,38.979l12.648,0l0,-29.854c0,-4.522 1.052,-8.269 3.155,-11.234c2.415,-3.368 5.858,-5.043 10.304,-5.043c1.34,0 2.674,0.118 3.984,0.354Zm-145.787,23.645c-0,-5.452 -1.598,-10.024 -4.819,-13.683c-3.197,-3.666 -8.19,-6.854 -14.991,-9.596c-5.534,-2.184 -9.368,-4.218 -11.465,-6.085c-2.421,-2.029 -3.641,-4.609 -3.641,-7.723c-0,-2.822 1.069,-5.198 3.178,-7.152c2.5,-2.183 5.997,-3.281 10.461,-3.281c5.643,-0 10.69,1.21 15.166,3.629l3.173,-10.303c-4.753,-2.494 -10.738,-3.747 -17.918,-3.747c-8.046,0 -14.511,2.072 -19.384,6.209c-4.873,4.144 -7.318,9.447 -7.318,15.917c-0,9.918 6.916,17.449 20.735,22.603c5.084,1.867 8.701,3.902 10.888,6.085c2.175,2.19 3.287,4.876 3.287,8.083c-0,3.579 -1.34,6.451 -3.99,8.597c-2.65,2.14 -6.357,3.219 -11.122,3.219c-6.633,0 -12.72,-1.638 -18.266,-4.925l-2.926,10.539c5.137,3.281 11.945,4.912 20.435,4.912c9.193,0 16.368,-2.301 21.499,-6.903c4.669,-4.218 7.018,-9.677 7.018,-16.395Zm88.056,-85.01l0,-16.754c0,-7.301 -3.863,-11.792 -9.734,-11.792c-3.797,-0 -7.192,1.904 -8.862,4.894l-0.169,0l-0.294,-4.317l-5.468,-0c0.174,2.872 0.234,5.527 0.234,7.995l0,19.974l6.219,-0l0,-16.636c0,-3.679 2.53,-6.848 6.213,-6.848c3.738,-0 5.636,2.481 5.636,7.424l0,16.06l6.225,-0Zm-237.923,-27.963l-6.105,0.006l-3.395,13.057c-0.865,3.282 -1.496,6.333 -1.898,9.212l-0.169,-0c-0.582,-2.934 -1.328,-6.042 -2.247,-9.212l-3.803,-13.057l-5.066,0l-3.971,13.343c-1.094,3.622 -1.845,6.562 -2.302,8.919l-0.12,0c-0.517,-2.822 -1.147,-5.812 -1.892,-8.975l-3.173,-13.293l-6.387,0l8.232,27.963l5.762,-0l3.851,-12.146c0.92,-2.927 1.791,-5.985 2.248,-9.031l0.114,0c0.601,3.046 1.346,6.06 2.247,9.031l3.623,12.146l5.702,-0l8.749,-27.963Zm49.343,27.963l-0,-40.797l-6.219,0l-0,40.797l6.219,-0Zm230.394,-23.305l0,-4.664l-6.838,-0l0,-6.96l-6.104,1.842l-0,5.124l-4.08,0l-0,4.658l4.08,0l-0,14.1c-0,7.077 2.932,9.782 7.829,9.782c2.007,-0 3.617,-0.23 4.825,-0.695l-0.174,-4.714c-0.739,0.167 -1.659,0.285 -2.758,0.285c-2.416,-0 -3.618,-1.724 -3.618,-5.179l0,-13.579l6.838,0Zm-340.274,23.937c8.352,0 13.934,-6.103 13.934,-14.843c0,-8.517 -5.414,-14.329 -13.471,-14.329c-8.52,0 -13.928,6.153 -13.928,14.788c-0,8.281 5.293,14.384 13.465,14.384Zm0.24,-24.631c4.369,-0 7.247,4.255 7.247,9.949c-0,5.589 -2.938,10.074 -7.307,10.074c-4.374,-0 -7.306,-4.2 -7.306,-9.956c-0,-5.868 2.872,-10.067 7.366,-10.067Zm412.504,100.003c0,-7.096 -1.682,-13.076 -5.017,-17.896c-4.218,-6.085 -10.425,-9.124 -18.633,-9.124c-8.346,-0 -14.985,3.039 -19.894,9.124c-4.693,5.701 -7.036,12.84 -7.036,21.419c-0,8.734 2.523,15.706 7.552,20.891c5.042,5.186 11.97,7.779 20.79,7.779c7.337,-0 13.694,-1.166 19.084,-3.517l-1.983,-8.765c-4.609,1.793 -9.734,2.686 -15.346,2.686c-5.077,-0 -9.211,-1.328 -12.408,-3.976c-3.527,-2.965 -5.366,-7.27 -5.504,-12.89l37.933,0c0.336,-1.892 0.492,-3.808 0.462,-5.731Zm-12.059,-3.282l-26.33,-0.012c0.385,-3.809 1.634,-7.096 3.744,-9.832c2.559,-3.436 5.93,-5.148 10.058,-5.148c4.518,-0 7.883,1.755 10.07,5.279c1.719,2.729 2.548,5.961 2.458,9.713Zm-85.611,5.502c0,-8.51 -2.409,-15.451 -7.264,-20.823c-5.078,-5.539 -11.783,-8.312 -20.135,-8.312c-8.665,0 -15.557,2.81 -20.676,8.43c-5.101,5.62 -7.667,12.84 -7.667,21.654c0,8.424 2.482,15.415 7.439,20.954c4.957,5.533 11.608,8.299 19.966,8.299c8.665,0 15.617,-2.878 20.844,-8.659c4.994,-5.614 7.493,-12.797 7.493,-21.543Zm-13.105,0.292c0,5.223 -1.165,9.726 -3.491,13.553c-2.74,4.584 -6.531,6.892 -11.368,6.892c-4.939,-0 -8.749,-2.258 -11.482,-6.78c-2.266,-3.728 -3.395,-8.207 -3.395,-13.429c-0,-5.372 1.129,-9.919 3.395,-13.659c2.643,-4.516 6.525,-6.78 11.59,-6.78c4.915,0 8.713,2.264 11.362,6.78c2.26,3.74 3.389,8.212 3.389,13.423Zm-209.232,-78.516l0.006,-16.748c0,-7.301 -3.851,-11.792 -9.722,-11.792c-3.803,0 -7.198,1.898 -8.869,4.888l-0.174,0l-0.288,-4.317l-5.468,-0c0.174,2.872 0.234,5.527 0.234,7.995l0,19.974l6.213,-0l0,-16.636c0,-3.679 2.53,-6.848 6.213,-6.848c3.743,-0 5.642,2.481 5.642,7.424l0,16.06l6.213,-0Zm-20.976,77.995c0,-8.666 -2.247,-15.65 -6.724,-20.966c-4.482,-5.303 -10.088,-7.958 -16.8,-7.958c-8.592,-0 -14.991,3.399 -19.203,10.191l-0.241,0l-0.703,-9.013l-11.122,0c0.307,6.389 0.469,12.623 0.469,18.696l0,61.266l12.642,-0l0,-29.849c3.293,5.378 8.713,8.064 16.254,8.064c6.982,0 12.834,-2.493 17.545,-7.493c5.257,-5.613 7.883,-13.262 7.883,-22.938Zm-12.882,0.465c-0,6.011 -1.328,10.849 -3.984,14.515c-2.65,3.66 -6.357,5.508 -11.122,5.508c-4.056,-0 -7.349,-1.433 -9.89,-4.28c-2.536,-2.853 -3.81,-6.346 -3.81,-10.483l0,-9.49c0.048,-1.303 0.241,-2.593 0.589,-3.846c0.775,-3.356 2.44,-6.067 4.975,-8.145c2.536,-2.053 5.372,-3.101 8.49,-3.101c4.603,0 8.274,1.873 11.008,5.62c2.5,3.598 3.744,8.163 3.744,13.702Zm-102.477,-78.119c14.901,-0 22.79,-7.245 22.79,-20.656c0,-11.63 -6.681,-18.764 -21.18,-18.764c-3.581,-0.006 -7.162,0.261 -10.708,0.807l0,38.148c2.536,0.291 5.576,0.465 9.098,0.465Zm2.121,-34.525c9.211,-0 14.102,5.011 14.102,14.037c-0,10.129 -5.408,15.538 -14.908,15.538c-2.018,-0 -3.455,-0.056 -4.2,-0.174l0,-28.998c1.382,-0.286 3.047,-0.403 5.006,-0.403Zm292.577,34.816c8.358,0 13.94,-6.103 13.934,-14.843c0,-8.517 -5.407,-14.329 -13.471,-14.329c-8.514,0 -13.922,6.153 -13.922,14.788c0,8.281 5.294,14.384 13.459,14.384Zm0.235,-24.631c4.38,-0 7.258,4.255 7.246,9.949c0,5.589 -2.932,10.074 -7.306,10.074c-4.369,-0 -7.301,-4.2 -7.301,-9.956c0,-5.868 2.872,-10.067 7.361,-10.067Zm-384.39,61.65c-0.174,-19.105 15.653,-28.403 16.374,-28.837c-8.959,-13.063 -22.851,-14.85 -27.73,-14.992c-11.669,-1.222 -22.983,6.984 -28.926,6.984c-6.062,0 -15.213,-6.86 -25.074,-6.662c-12.69,0.199 -24.563,7.543 -31.076,18.95c-13.441,23.261 -3.419,57.445 9.457,76.246c6.442,9.211 13.97,19.483 23.819,19.123c9.637,-0.39 13.237,-6.134 24.869,-6.134c11.525,-0 14.902,6.134 24.948,5.905c10.347,-0.161 16.854,-9.249 23.073,-18.541c7.445,-10.551 10.431,-20.947 10.551,-21.48c-0.24,-0.087 -20.081,-7.661 -20.285,-30.562Zm-18.975,-56.192c5.179,-6.482 8.731,-15.302 7.745,-24.247c-7.505,0.335 -16.884,5.186 -22.292,11.525c-4.777,5.589 -9.049,14.744 -7.943,23.348c8.424,0.632 17.076,-4.249 22.49,-10.626Zm289.249,96.536c-0,-8.666 -2.253,-15.65 -6.724,-20.966c-4.494,-5.303 -10.1,-7.958 -16.806,-7.958c-8.598,-0 -14.997,3.399 -19.203,10.191l-0.241,0l-0.703,-9.013l-11.122,0c0.307,6.389 0.469,12.623 0.469,18.696l-0,61.266l12.642,-0l0,-29.849c3.293,5.378 8.713,8.064 16.253,8.064c6.988,0 12.835,-2.493 17.546,-7.493c5.263,-5.613 7.889,-13.262 7.889,-22.938Zm-12.889,0.465c0,6.011 -1.327,10.849 -3.983,14.515c-2.644,3.66 -6.351,5.508 -11.122,5.508c-4.068,-0 -7.367,-1.439 -9.902,-4.28c-2.524,-2.853 -3.804,-6.346 -3.804,-10.483l0,-9.49c0,-1.005 0.21,-2.283 0.595,-3.846c0.775,-3.356 2.433,-6.067 4.969,-8.145c2.548,-2.053 5.378,-3.101 8.502,-3.101c4.603,0 8.262,1.873 10.996,5.62c2.5,3.598 3.749,8.163 3.749,13.702Z"/></svg>
              </a>-->
              <a id="playstore" href="https://play.google.com/store/apps/details?id=com.rouilleux.thefoldapp">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 237"><path d="M770.371,237.019l-740.741,-0c-16.593,-0 -29.63,-13.037 -29.63,-29.63l0,-177.778c0,-16.592 13.037,-29.63 29.63,-29.63l740.741,0c16.593,0 29.63,13.038 29.63,29.63l-0,177.778c-0,16.593 -13.037,29.63 -29.63,29.63Zm-487.704,-100.149l-0,10.667l25.481,0c-0.592,5.926 -2.963,10.667 -5.926,13.63c-3.555,3.555 -9.481,7.703 -19.555,7.703c-16,0 -27.852,-12.444 -27.852,-28.444c-0,-16 12.444,-28.444 27.852,-28.444c8.296,-0 14.815,3.555 19.555,7.703l7.704,-7.703c-6.518,-5.926 -14.815,-10.667 -26.667,-10.667c-21.333,-0 -39.703,17.778 -39.703,39.111c-0,21.333 18.37,39.111 39.703,39.111c11.852,0 20.149,-3.555 27.26,-11.259c7.111,-7.111 9.481,-17.185 9.481,-24.889c0,-2.37 0,-4.741 -0.592,-6.519l-36.741,0Zm320.593,-33.185l-26.667,0l-0,74.074l11.259,0l0,-27.851l15.408,-0c12.444,-0 24.296,-8.889 24.296,-23.112c-0,-14.222 -11.852,-23.111 -24.296,-23.111Zm0.592,35.556l-16,-0l0,-25.482l16,0c8.297,0 13.037,7.111 13.037,12.445c-0.592,6.518 -5.333,13.037 -13.037,13.037Zm-52.148,5.926c-2.37,-5.926 -8.296,-16 -21.333,-16c-13.037,-0 -23.704,10.074 -23.704,25.481c0,14.222 10.667,25.482 24.889,25.482c11.259,-0 18.37,-7.111 20.741,-11.26l-8.297,-5.925c-2.963,4.148 -6.518,7.111 -12.444,7.111c-5.926,-0 -9.482,-2.371 -12.445,-7.704l33.778,-14.222l-1.185,-2.963Zm-34.37,8.296c-0,-9.481 7.703,-14.815 13.037,-14.815c4.148,0 8.296,2.371 9.481,5.334l-22.518,9.481Zm-45.63,-18.963c-2.963,-2.963 -7.704,-5.926 -13.63,-5.926c-12.444,0 -24.296,11.259 -24.296,25.482c0,14.222 11.259,24.889 24.296,24.889c5.926,-0 10.667,-2.963 13.037,-5.926l0.593,-0l0,3.555c0,9.482 -5.333,14.815 -13.63,14.815c-6.518,0 -11.259,-4.741 -12.444,-8.889l-9.482,4.148c2.963,6.519 10.075,14.815 22.519,14.815c13.037,0 23.704,-7.704 23.704,-26.074l-0,-45.037l-10.667,-0l0,4.148Zm-13.037,34.963c-7.704,0 -14.222,-6.518 -14.222,-15.407c-0,-8.889 6.518,-15.408 14.222,-15.408c7.704,0 13.63,6.519 13.63,15.408c-0,8.889 -5.926,15.407 -13.63,15.407Zm-150.519,-123.852l0,-4.148l-20.74,-0l-0,35.556l20.74,-0l0,-4.741l-16,-0l0,-11.259l14.815,-0l0,-4.149l-14.815,0l0,-11.259l16,0Zm19.556,31.408l-0,-31.408l10.074,0l0,-4.148l-24.889,-0l0,4.148l10.074,0l0,31.408l4.741,-0Zm27.259,-0l4.741,-0l-0,-35.556l-4.741,-0l0,35.556Zm81.185,-4.741c3.556,-3.556 5.334,-7.704 5.334,-13.037c-0,-5.334 -1.778,-9.482 -5.334,-13.037c-3.555,-3.556 -7.703,-5.334 -13.037,-5.334c-5.333,0 -9.481,1.778 -13.037,5.334c-3.555,3.555 -5.333,7.703 -5.333,13.037c0,5.333 1.778,9.481 5.333,13.037c3.556,3.555 7.704,5.333 13.037,5.333c5.334,0 9.482,-1.778 13.037,-5.333Zm-22.518,-2.963c-2.963,-2.371 -4.148,-5.926 -4.148,-10.074c-0,-4.148 1.778,-7.704 4.148,-10.074c2.37,-2.963 5.926,-4.148 9.481,-4.148c3.556,-0 7.112,1.777 9.482,4.148c2.963,2.37 4.148,5.926 4.148,10.074c0,4.148 -1.778,7.703 -4.148,10.074c-2.37,2.963 -5.926,4.148 -9.482,4.148c-3.555,0 -7.111,-1.778 -9.481,-4.148Zm34.37,7.704l4.148,-0l0,-29.037l18.371,29.037l4.741,-0l-0,-35.556l-4.741,-0l-0,27.852l-17.185,-27.852l-5.334,-0l0,35.556Zm-389.043,-22.655l0,129.049c0,0.776 0.64,1.416 1.422,1.416c0.374,0 0.729,-0.148 0.996,-0.409l67.194,-65.529l-67.194,-65.535c-0.267,-0.26 -0.622,-0.409 -0.996,-0.409c-0.782,0 -1.422,0.64 -1.422,1.417Zm97.784,37.6l-84.249,-46.418l-0.053,-0.03c-1.452,-0.788 -2.833,1.174 -1.642,2.317l66.045,63.153l19.899,-19.022Zm-85.938,97.979c-1.197,1.144 0.184,3.111 1.641,2.323l0.054,-0.03l84.243,-46.417l-19.899,-19.029l-66.039,63.153Zm119.953,-79.259l-23.526,-12.96l-22.122,21.167l22.122,21.156l23.526,-12.948c6.4,-3.538 6.4,-12.883 -0,-16.415Zm188.504,-33.665l-0,-31.408l10.074,0l-0,-4.148l-24.297,-0l0,4.148l10.074,0l0,31.408l4.149,-0Zm-98.371,-16.593l0,-2.37l-17.185,-0l-0,4.74l13.037,0c-0,2.963 -1.185,5.334 -2.963,7.111c-2.963,2.963 -5.926,4.149 -10.074,4.149c-3.556,-0 -6.519,-1.778 -9.482,-4.149c-2.963,-2.37 -4.148,-5.925 -4.148,-10.074c0,-4.148 1.185,-7.703 4.148,-10.074c2.371,-2.963 5.926,-4.148 9.482,-4.148c4.148,0 7.111,1.185 9.481,4.148l2.963,-2.963c-1.185,-1.777 -2.963,-2.963 -5.333,-4.148c-2.37,-1.185 -4.741,-1.778 -7.111,-1.778c-5.333,0 -9.482,1.778 -13.037,5.334c-3.556,3.555 -5.333,7.703 -5.333,13.037c-0,5.333 1.777,9.481 5.333,13.037c3.555,3.555 7.704,5.333 13.037,5.333c5.333,0 9.481,-1.778 13.037,-5.333c2.963,-2.963 4.148,-7.111 4.148,-11.852Zm122.667,68.741c-14.222,-0 -25.482,10.666 -25.482,25.481c0,14.222 11.26,25.482 25.482,25.482c14.222,-0 25.481,-10.667 25.481,-25.482c0,-15.407 -11.259,-25.481 -25.481,-25.481Zm-0,40.296c-7.704,0 -14.222,-6.518 -14.222,-15.407c-0,-8.889 6.518,-15.408 14.222,-15.408c7.704,0 14.222,5.926 14.222,15.408c0,8.889 -6.518,15.407 -14.222,15.407Zm85.926,8.296l11.259,0l0,-74.074l-11.259,0l-0,74.074Zm144,0l11.259,0l0,-74.074l-11.259,0l-0,74.074Zm-285.037,-48.592c-14.223,-0 -25.482,10.666 -25.482,25.481c0,14.222 11.259,25.482 25.482,25.482c14.222,-0 25.481,-10.667 25.481,-25.482c0,-15.407 -11.259,-25.481 -25.481,-25.481Zm-0,40.296c-7.704,0 -14.223,-6.518 -14.223,-15.407c0,-8.889 6.519,-15.408 14.223,-15.408c7.703,0 14.222,5.926 14.222,15.408c-0,8.889 -6.519,15.407 -14.222,15.407Zm384.593,-39.111l-12.445,32l-0.593,-0l-13.037,-32l-11.851,-0l19.555,45.037l-11.259,24.889l11.259,-0l30.222,-69.926l-11.851,-0Zm-61.038,-1.778c-8.296,0 -16.592,3.556 -19.555,11.259l10.074,4.149c2.37,-4.149 5.926,-5.334 10.074,-5.334c5.926,0 11.259,3.556 11.852,9.482l-0,0.592c-1.778,-1.185 -6.519,-2.963 -11.259,-2.963c-10.667,0 -21.334,5.926 -21.334,16.593c0,10.074 8.889,16.593 18.371,16.593c7.703,-0 11.259,-3.556 14.222,-7.112l0.593,0l-0,5.926l10.666,0l0,-28.444c-1.185,-13.037 -11.259,-20.741 -23.704,-20.741Zm-1.185,40.889c-3.555,0 -8.889,-1.778 -8.889,-6.518c0,-5.926 6.519,-7.704 11.852,-7.704c4.741,-0 7.111,1.185 10.074,2.37c-1.185,7.111 -7.111,11.852 -13.037,11.852Z"/></svg>
              </a>
              <a id="android-direct" href="https://expo.dev/artifacts/eas/c9iz-G7_sKAIwZjIQ5-_YbdHPxeYCrruLw8I7sBclXQ.aab">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 237"><path d="M770.37,237.019L29.63,237.019C13.037,237.019 0,223.981 0,207.389L0,29.611C0,13.019 13.037,-0.019 29.63,-0.019L770.37,-0.019C786.963,-0.019 800,13.019 800,29.611L800,207.389C800,223.981 786.963,237.019 770.37,237.019ZM301.658,120.001L309.394,120.001L309.394,84.108L346.781,123.122L346.781,68.735L339.044,68.735L339.044,104.362L301.658,65.249L301.658,120.001ZM398.146,138.376L406.443,138.376L406.443,133.243L384.365,133.243L384.365,138.376L392.685,138.376L392.685,169.431L398.146,169.431L398.146,138.376ZM381.271,135.188C378.24,133.423 375.099,132.54 371.849,132.54C365.896,132.54 361.044,134.751 357.294,139.173C354.247,142.782 352.724,146.892 352.724,151.501C352.724,156.642 354.544,161.032 358.185,164.673C361.841,168.282 366.255,170.087 371.427,170.087C374.865,170.087 378.146,169.204 381.271,167.438L381.271,160.853C380.38,161.603 379.533,162.243 378.728,162.774C377.923,163.306 377.146,163.728 376.396,164.04C375.052,164.665 373.419,164.978 371.497,164.978C367.779,164.978 364.638,163.673 362.076,161.063C359.513,158.454 358.232,155.251 358.232,151.454C358.232,147.61 359.513,144.36 362.076,141.704C364.622,139.032 367.755,137.696 371.474,137.696C374.849,137.696 378.115,139.017 381.271,141.657L381.271,135.188ZM347.357,138.376L347.357,133.243L327.388,133.243L327.388,169.431L347.357,169.431L347.357,164.298L332.849,164.298L332.849,152.204L346.935,152.204L346.935,147.071L332.849,147.071L332.849,138.376L347.357,138.376ZM310.747,154.009C313.154,153.493 315.091,152.313 316.56,150.47C318.029,148.626 318.763,146.423 318.763,143.86C318.763,140.579 317.591,137.938 315.247,135.938C313.122,134.142 309.669,133.243 304.888,133.243L298.49,133.243L298.49,169.431L303.951,169.431L303.951,154.618L304.935,154.618L315.271,169.431L321.951,169.431L310.747,154.009ZM303.951,149.86L303.951,138.376L305.896,138.376C310.927,138.376 313.443,140.228 313.443,143.931C313.443,147.884 310.857,149.86 305.685,149.86L303.951,149.86ZM289.794,133.243L284.333,133.243L284.333,169.431L289.794,169.431L289.794,133.243ZM249.482,169.431L256.982,169.431C260.654,169.431 263.49,169.095 265.49,168.423C267.63,167.751 269.677,166.556 271.63,164.837C275.521,161.399 277.466,156.899 277.466,151.337C277.466,145.79 275.599,141.313 271.865,137.907C270.021,136.22 267.966,135.024 265.701,134.321C263.591,133.603 260.716,133.243 257.076,133.243L249.482,133.243L249.482,169.431ZM254.943,164.298L254.943,138.376L257.404,138.376C262.06,138.376 265.583,139.478 267.974,141.681C270.63,144.149 271.958,147.368 271.958,151.337C271.958,155.274 270.615,158.47 267.927,160.923C266.615,162.11 265.146,162.978 263.521,163.524C261.896,164.04 259.857,164.298 257.404,164.298L254.943,164.298ZM694.218,99.183L697.804,99.183C703.139,99.183 707.378,97.81 710.521,95.065C713.664,92.321 715.236,88.602 715.236,83.909C715.236,79.526 713.764,75.896 710.82,73.019C709.137,71.358 707.189,70.229 704.976,69.632C702.785,69.034 699.542,68.735 695.247,68.735L686.482,68.735L686.482,120.001L694.218,120.001L694.218,99.183ZM694.218,92.011L694.218,75.874L696.775,75.874C704.102,75.874 707.765,78.519 707.765,83.81C707.765,89.277 704.212,92.011 697.107,92.011L694.218,92.011ZM657.495,99.183L661.081,99.183C666.416,99.183 670.655,97.81 673.798,95.065C676.941,92.321 678.513,88.602 678.513,83.909C678.513,79.526 677.041,75.896 674.097,73.019C672.415,71.358 670.467,70.229 668.253,69.632C666.062,69.034 662.819,68.735 658.525,68.735L649.759,68.735L649.759,120.001L657.495,120.001L657.495,99.183ZM657.495,92.011L657.495,75.874L660.052,75.874C667.379,75.874 671.042,78.519 671.042,83.81C671.042,89.277 667.49,92.011 660.384,92.011L657.495,92.011ZM690.177,169.431L697.677,169.431C701.349,169.431 704.185,169.095 706.185,168.423C708.326,167.751 710.372,166.556 712.326,164.837C716.216,161.399 718.161,156.899 718.161,151.337C718.161,145.79 716.294,141.313 712.56,137.907C710.716,136.22 708.661,135.024 706.396,134.321C704.286,133.603 701.411,133.243 697.771,133.243L690.177,133.243L690.177,169.431ZM695.638,164.298L695.638,138.376L698.099,138.376C702.755,138.376 706.279,139.478 708.669,141.681C711.326,144.149 712.654,147.368 712.654,151.337C712.654,155.274 711.31,158.47 708.622,160.923C707.31,162.11 705.841,162.978 704.216,163.524C702.591,164.04 700.552,164.298 698.099,164.298L695.638,164.298ZM504.107,133.243L498.177,133.243L513.552,171.751L523.255,146.087L532.56,171.728L548.732,133.243L542.802,133.243L532.794,157.431L523.372,131.485L513.529,157.431L504.107,133.243ZM629.505,107.55L634.884,120.001L643.351,120.001L618.88,65.515L593.513,120.001L601.847,120.001L607.558,107.55L629.505,107.55ZM626.351,100.278L610.779,100.278L618.747,82.847L626.351,100.278ZM281.404,107.55L286.783,120.001L295.249,120.001L270.779,65.515L245.411,120.001L253.745,120.001L259.456,107.55L281.404,107.55ZM278.249,100.278L262.677,100.278L270.646,82.847L278.249,100.278ZM359.066,120.001L369.691,120.001C374.893,120.001 378.91,119.525 381.743,118.573C384.776,117.621 387.676,115.928 390.443,113.493C395.954,108.623 398.71,102.248 398.71,94.368C398.71,86.51 396.065,80.168 390.775,75.343C388.163,72.952 385.252,71.259 382.042,70.263C379.054,69.244 374.981,68.735 369.824,68.735L359.066,68.735L359.066,120.001ZM366.802,112.729L366.802,76.007L370.288,76.007C376.885,76.007 381.876,77.567 385.263,80.688C389.026,84.186 390.908,88.746 390.908,94.368C390.908,99.946 389.004,104.473 385.197,107.948C383.337,109.631 381.257,110.859 378.954,111.634C376.652,112.364 373.764,112.729 370.288,112.729L366.802,112.729ZM161.597,100.76L174.809,77.874C175.542,76.603 175.105,74.955 173.839,74.215C173.432,73.976 172.969,73.85 172.497,73.85C171.547,73.85 170.667,74.361 170.196,75.186L156.819,98.368C146.606,93.702 135.111,91.104 122.748,91.104C110.386,91.104 98.891,93.705 88.678,98.368L75.301,75.186C74.823,74.362 73.939,73.853 72.986,73.853C71.518,73.853 70.31,75.061 70.31,76.53C70.31,77.002 70.435,77.465 70.672,77.874L83.853,100.76C61.081,113.092 45.648,136.153 43.1,163.15L202.378,163.15C199.83,136.153 184.397,113.092 161.597,100.76ZM86.17,140.782C82.508,140.782 79.494,137.769 79.494,134.106C79.494,130.444 82.508,127.43 86.17,127.43C89.833,127.43 92.846,130.444 92.846,134.106C92.845,137.768 89.832,140.78 86.17,140.782ZM159.277,140.782C155.625,140.767 152.629,137.758 152.629,134.106C152.629,130.444 155.642,127.43 159.305,127.43C162.967,127.43 165.981,130.444 165.981,134.106C165.979,137.769 162.964,140.782 159.302,140.782L159.277,140.782ZM611.755,151.173C611.755,156.97 613.802,161.657 617.896,165.235C621.615,168.47 625.81,170.087 630.482,170.087C635.763,170.087 640.271,168.267 644.005,164.626C647.724,161.001 649.583,156.587 649.583,151.384C649.583,146.212 647.732,141.782 644.029,138.095C640.341,134.407 635.919,132.563 630.763,132.563C625.544,132.563 621.076,134.392 617.357,138.048C613.622,141.704 611.755,146.079 611.755,151.173ZM617.263,151.243C617.263,147.431 618.56,144.228 621.154,141.634C623.747,139.009 626.927,137.696 630.693,137.696C634.474,137.696 637.646,139.009 640.208,141.634C642.786,144.243 644.076,147.478 644.076,151.337C644.076,155.149 642.771,158.384 640.161,161.04C637.552,163.665 634.349,164.978 630.552,164.978C627.052,164.978 623.966,163.688 621.294,161.11C618.607,158.532 617.263,155.243 617.263,151.243ZM675.88,160.642L679.677,169.431L685.654,169.431L668.38,130.97L650.474,169.431L656.357,169.431L660.388,160.642L675.88,160.642ZM673.654,155.509L662.661,155.509L668.286,143.204L673.654,155.509ZM516.714,68.735L508.978,68.735L508.978,120.001L516.714,120.001L516.714,68.735ZM459.177,151.173C459.177,156.97 461.224,161.657 465.318,165.235C469.036,168.47 473.232,170.087 477.904,170.087C483.185,170.087 487.693,168.267 491.427,164.626C495.146,161.001 497.005,156.587 497.005,151.384C497.005,146.212 495.154,141.782 491.451,138.095C487.763,134.407 483.341,132.563 478.185,132.563C472.966,132.563 468.497,134.392 464.779,138.048C461.044,141.704 459.177,146.079 459.177,151.173ZM464.685,151.243C464.685,147.431 465.982,144.228 468.576,141.634C471.169,139.009 474.349,137.696 478.115,137.696C481.896,137.696 485.068,139.009 487.63,141.634C490.208,144.243 491.497,147.478 491.497,151.337C491.497,155.149 490.193,158.384 487.583,161.04C484.974,163.665 481.771,164.978 477.974,164.978C474.474,164.978 471.388,163.688 468.716,161.11C466.029,158.532 464.685,155.243 464.685,151.243ZM425.804,98.153C429.213,97.423 431.958,95.752 434.038,93.14C436.119,90.528 437.16,87.407 437.16,83.776C437.16,79.128 435.499,75.387 432.179,72.554C429.169,70.008 424.277,68.735 417.503,68.735L408.439,68.735L408.439,120.001L416.175,120.001L416.175,99.017L417.57,99.017L432.212,120.001L441.675,120.001L425.804,98.153ZM416.175,92.276L416.175,76.007L418.931,76.007C426.059,76.007 429.622,78.63 429.622,83.876C429.622,89.476 425.959,92.276 418.632,92.276L416.175,92.276ZM445.66,94.136C445.66,102.348 448.559,108.989 454.359,114.058C459.627,118.64 465.57,120.931 472.189,120.931C479.671,120.931 486.057,118.352 491.347,113.194C496.615,108.059 499.249,101.806 499.249,94.435C499.249,87.108 496.626,80.832 491.38,75.608C486.156,70.384 479.892,67.772 472.587,67.772C465.194,67.772 458.863,70.362 453.595,75.542C448.305,80.722 445.66,86.92 445.66,94.136ZM453.462,94.235C453.462,88.834 455.299,84.297 458.974,80.622C462.648,76.903 467.153,75.044 472.488,75.044C477.844,75.044 482.338,76.903 485.968,80.622C489.62,84.319 491.447,88.901 491.447,94.368C491.447,99.769 489.598,104.351 485.902,108.114C482.205,111.833 477.667,113.692 472.288,113.692C467.33,113.692 462.958,111.866 459.173,108.214C455.366,104.562 453.462,99.902 453.462,94.235ZM529.033,120.001L539.658,120.001C544.859,120.001 548.877,119.525 551.71,118.573C554.743,117.621 557.643,115.928 560.41,113.493C565.921,108.623 568.677,102.248 568.677,94.368C568.677,86.51 566.032,80.168 560.742,75.343C558.13,72.952 555.219,71.259 552.009,70.263C549.021,69.244 544.948,68.735 539.79,68.735L529.033,68.735L529.033,120.001ZM536.769,112.729L536.769,76.007L540.255,76.007C546.852,76.007 551.843,77.567 555.23,80.688C558.993,84.186 560.874,88.746 560.874,94.368C560.874,99.946 558.971,104.473 555.163,107.948C553.304,109.631 551.223,110.859 548.921,111.634C546.619,112.364 543.73,112.729 540.255,112.729L536.769,112.729ZM553.232,169.431L558.693,169.431L558.693,144.095L585.083,171.634L585.083,133.243L579.622,133.243L579.622,158.392L553.232,130.782L553.232,169.431ZM599.216,133.243L593.755,133.243L593.755,169.431L609.857,169.431L609.857,164.298L599.216,164.298L599.216,133.243ZM426.13,169.431L433.63,169.431C437.302,169.431 440.138,169.095 442.138,168.423C444.279,167.751 446.326,166.556 448.279,164.837C452.169,161.399 454.115,156.899 454.115,151.337C454.115,145.79 452.247,141.313 448.513,137.907C446.669,136.22 444.615,135.024 442.349,134.321C440.24,133.603 437.365,133.243 433.724,133.243L426.13,133.243L426.13,169.431ZM431.591,164.298L431.591,138.376L434.052,138.376C438.708,138.376 442.232,139.478 444.622,141.681C447.279,144.149 448.607,147.368 448.607,151.337C448.607,155.274 447.263,158.47 444.576,160.923C443.263,162.11 441.794,162.978 440.169,163.524C438.544,164.04 436.505,164.298 434.052,164.298L431.591,164.298Z"/></svg>
              </a>
            </div>
          </div>
          <div id="menu">
            <div id="language-toggle-wrapper">
              <button id="language-toggle" @click=${this._toggleLanguage}>${ this.currentLanguage === "cs" ? "EN" : "CZ" }</button>
            </div>
            <div id="socials">
              <div class="social">
                <a href="https://instagram.com/rouilleux" target="_blank">
                  <svg xmlns="http://www.w3.org/2000/svg" class="ionicon" viewBox="0 0 512 512"><path d="M349.33 69.33a93.62 93.62 0 0193.34 93.34v186.66a93.62 93.62 0 01-93.34 93.34H162.67a93.62 93.62 0 01-93.34-93.34V162.67a93.62 93.62 0 0193.34-93.34h186.66m0-37.33H162.67C90.8 32 32 90.8 32 162.67v186.66C32 421.2 90.8 480 162.67 480h186.66C421.2 480 480 421.2 480 349.33V162.67C480 90.8 421.2 32 349.33 32z"/><path d="M377.33 162.67a28 28 0 1128-28 27.94 27.94 0 01-28 28zM256 181.33A74.67 74.67 0 11181.33 256 74.75 74.75 0 01256 181.33m0-37.33a112 112 0 10112 112 112 112 0 00-112-112z"/></svg>
                </a>
              </div>
              <div class="social">
                <a href="https://www.facebook.com/rouilleux" target="_blank">
                  <svg xmlns="http://www.w3.org/2000/svg" class="ionicon" viewBox="0 0 512 512"><path d="M480 257.35c0-123.7-100.3-224-224-224s-224 100.3-224 224c0 111.8 81.9 204.47 189 221.29V322.12h-56.89v-64.77H221V208c0-56.13 33.45-87.16 84.61-87.16 24.51 0 50.15 4.38 50.15 4.38v55.13H327.5c-27.81 0-36.51 17.26-36.51 35v42h62.12l-9.92 64.77H291v156.54c107.1-16.81 189-109.48 189-221.31z" fill-rule="evenodd"/></svg>
                </a>
              </div>
              <div class="social">
                <a href="https://www.youtube.com/@rouilleux" target="_blank">
                  <svg xmlns="http://www.w3.org/2000/svg" class="ionicon" viewBox="0 0 512 512"><path d="M508.64 148.79c0-45-33.1-81.2-74-81.2C379.24 65 322.74 64 265 64h-18c-57.6 0-114.2 1-169.6 3.6C36.6 67.6 3.5 104 3.5 149 1 184.59-.06 220.19 0 255.79q-.15 53.4 3.4 106.9c0 45 33.1 81.5 73.9 81.5 58.2 2.7 117.9 3.9 178.6 3.8q91.2.3 178.6-3.8c40.9 0 74-36.5 74-81.5 2.4-35.7 3.5-71.3 3.4-107q.34-53.4-3.26-106.9zM207 353.89v-196.5l145 98.2z"/></svg>
                </a>
              </div>
            </div>
            <div id="infotext-toggle-wrapper">
              <a id="infotext-toggle" href="/manifest.html" target="_blank">
                <svg xmlns="http://www.w3.org/2000/svg" class="ionicon" viewBox="0 0 512 512"><path d="M256 56C145.72 56 56 145.72 56 256s89.72 200 200 200 200-89.72 200-200S366.28 56 256 56zm0 82a26 26 0 11-26 26 26 26 0 0126-26zm64 226H200v-32h44v-88h-32v-32h64v120h44z"/></svg>
              </a>
            </div>
          </div>
          <div id="album-cover-wrapper"}>
            <img id="album-cover" src="../assets/thefold-cover.jpg" alt="Rouilleux - The Fold" />
          </div>
        </div>
        <div id="track-info">
          ${this.currentTrack.trackNameVersion}<br />
          <span class="version-name">(${this.currentTrack.trackNameDisplay || this.currentTrack.trackName} v${this.currentTrack.trackVersion})</span>
        </div>
        <div id="controls">
          <button id="previous" class="${this.activeTrackIndex === 1 ? "disabled" : ""}" @click=${this._onPreviousClick}>
            <svg xmlns="http://www.w3.org/2000/svg" class="ionicon" viewBox="0 0 512 512"><path d="M143.47 64v163.52L416 64v384L143.47 284.48V448H96V64h47.47z"/></svg>
          </button>
          <button id="play" @click=${this._onPlayClick} style="display:${ this.isPlaying ? 'none' : 'inline-block' };">
            <svg xmlns="http://www.w3.org/2000/svg" class="ionicon" viewBox="0 0 512 512"><path d="M96 448l320-192L96 64v384z"/></svg>
          </button>
          <button id="pause" @click=${this._onPauseClick} style="display:${ this.isPlaying ? 'inline-block' : 'none' };">
            <svg xmlns="http://www.w3.org/2000/svg" class="ionicon" viewBox="0 0 512 512"><path d="M224 432h-80V80h80zM368 432h-80V80h80z"/></svg>
          </button>
          <button id="next" class="${this.activeTrackIndex === this.tracklist.length ? "disabled" : ""}" @click=${this._onNextClick}>
            <svg xmlns="http://www.w3.org/2000/svg" class="ionicon" viewBox="0 0 512 512"><path d="M368.53 64v163.52L96 64v384l272.53-163.52V448H416V64h-47.47z"/></svg>
          </button>
        </div>
        <div id="track-timing">
          <span id="timing-current">${ secondsToTime(this.currentTrackProgressSeconds) }</span> / <span id="timing-total">${ secondsToTime(this.currentTrack?.duration) }</span>
        </div>
        <div id="track-progress-wrapper">
          <div id="track-progress-bar">
            <div id="progress" style="width:${this.currentTrackProgressPercentage}%;"></div>
            <div id="handle" style="left:${this.currentTrackProgressPercentage}%;"></div>
          </div>
        </div>
        <ol id="playlist">
          ${this.tracklist.map((track, i) =>
            html`
              <li class="track-info ${ this.activeTrackIndex == i+1 ? 'active' : ''}" data-index="${i+1}" @click=${this._onTrackClick}>
                <span class="track-name">${track.trackNameVersion}</span> <span class="version-name">(${track.trackNameDisplay || track.trackName} v${track.trackVersion})</span>
                <!-- <span class="track-duration">${secondsToTime(track.duration)}</span> -->
              </li>`
          )}
        </ol>
        <div id="lyrics">
          <h2>${ locales[this.currentLanguage].lyrics }</h2>
          <div id="lyrics-content">${ this.currentTrack?.lyrics ? unsafeHTML(lyrics[this.currentTrack.lyrics]) : "-" }</div>
        </div>
        <div class="horizontal-separator"></div>
        <div id="regenerate-wrapper">
          <button @click=${this._onRegenerateClick}>${ locales[this.currentLanguage].regenerate }</button>
        </div>
        <div id="footer">
          <div id="links">
            <a href="https://thefold-legacy.rouilleux.net" target="_blank">${ locales[this.currentLanguage].legacyapp }</a>
            <a href="/privacy.html" target="_blank">${ locales[this.currentLanguage].privacy }</a>
            <a href="/support.html" target="_blank">${ locales[this.currentLanguage].support }</a>
          </div>
          <div id="copyright">
            &copy; 2023-2026 <a href="https://rouilleux.net" target="_blank">Rouilleux</a>. ${ locales[this.currentLanguage].allrightsreserved }
          </div>
        </div>
        <div id="regenerate-after-play-wrapper">
          <div id="regenerate-question">
            ${ locales[this.currentLanguage].playbackfinished }<br>
            ${ locales[this.currentLanguage].playbackfinishedquestion }
          </div>
          <div id="regenerate-choice">
            <button id="rcb-yes" class="regenerate-choice-button" @click=${this._onRegenerateClick}>
              ${ locales[this.currentLanguage].yes }
            </button>
            <button id="rcb-no" class="regenerate-choice-button" @click=${this._closeRegenerateAfterPlay}>
              ${ locales[this.currentLanguage].no }
            </button>
          </div>
          <a id="regenerate-why" href="/manifest.html" target="_blank">
            ${ locales[this.currentLanguage].whatdoesitmean }
          </a>
        </div>
        <div id="audioElements" style="text-align:center; display: none;">
          ${this.tracklist.map((track, i) =>
            html`<audio 
              id="audio${i+1}"
              preload="auto"
              src=${track.filename}
              style="display:${ this.activeTrackIndex == i + 1 ? 'inline-block' : 'none' };"
              @ended=${this._onPlaybackEnded}
              @timeupdate=${this._onAudioTimeupdate}
            ></audio>`
          )}
        </div>
      </main>
    `;
  }
}
